import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TopNav from './TopNav';
import { getAccounts, createAccount, deleteAccount } from '../services/api';

interface Account {
  id: string;
  name?: string;
  accountType: 'INDIVIDUAL' | 'JOINT' | 'TRUST' | 'CORPORATE';
  environment: 'PAPER' | 'LIVE';
  status: 'ACTIVE' | 'INACTIVE' | 'PAUSED';
  alpacaKey?: string;
  alpacaSecret?: string;
  createdAt: string;
}



/**
 * Alpaca Keys Management Page
 * Allows users to add and manage their Alpaca trading accounts
 */
const AlpacaKeys: React.FC = () => {
  const { user: _user } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    accountType: 'INDIVIDUAL' as const,
    environment: 'PAPER' as const,
    alpacaKey: '',
    alpacaSecret: ''
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getAccounts();
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch accounts');
      
      // Fallback to empty array if API fails
      setAccounts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.alpacaKey || !formData.alpacaSecret) {
      setError('Alpaca API key and secret are required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      const requestData = {
        name: formData.name || undefined,
        accountType: formData.accountType,
        environment: formData.environment,
        alpacaKey: formData.alpacaKey,
        alpacaSecret: formData.alpacaSecret
      };

      await createAccount(requestData);
      setSuccess('Account added successfully!');
      setShowAddForm(false);
      setFormData({
        name: '',
        accountType: 'INDIVIDUAL',
        environment: 'PAPER',
        alpacaKey: '',
        alpacaSecret: ''
      });
      // Refresh the accounts list
      await fetchAccounts();
    } catch (error) {
      console.error('Failed to add account:', error);
      setError(error instanceof Error ? error.message : 'Failed to add account');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      return;
    }

    try {
      setError(null);
      
      await deleteAccount(accountId);
      setSuccess('Account deleted successfully!');
      await fetchAccounts();
    } catch (error) {
      console.error('Failed to delete account:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete account');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'text-green-500 bg-green-100';
      case 'INACTIVE':
        return 'text-red-500 bg-red-100';
      case 'PAUSED':
        return 'text-yellow-500 bg-yellow-100';
      default:
        return 'text-gray-500 bg-gray-100';
    }
  };

  const getEnvironmentColor = (environment: string) => {
    switch (environment) {
      case 'PAPER':
        return 'text-blue-500 bg-blue-100';
      case 'LIVE':
        return 'text-purple-500 bg-purple-100';
      default:
        return 'text-gray-500 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-950">
        <TopNav />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            <span className="ml-3 text-neutral-400">Loading accounts...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      <TopNav />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Alpaca Trading Accounts</h1>
              <p className="text-neutral-400">Manage your Alpaca trading accounts and API keys</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Add Account
            </button>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-900/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg mb-6">
              {success}
            </div>
          )}

          {/* Add Account Form */}
          {showAddForm && (
            <div className="bg-neutral-900 border border-neutral-700 rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold text-white mb-4">Add New Account</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Account Name (Optional)
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      placeholder="e.g., Paper Trading Account"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Account Type
                    </label>
                    <select
                      name="accountType"
                      value={formData.accountType}
                      onChange={handleInputChange}
                      className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="INDIVIDUAL">Individual</option>
                      <option value="JOINT">Joint</option>
                      <option value="TRUST">Trust</option>
                      <option value="CORPORATE">Corporate</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Environment
                    </label>
                    <select
                      name="environment"
                      value={formData.environment}
                      onChange={handleInputChange}
                      className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="PAPER">Paper Trading</option>
                      <option value="LIVE">Live Trading</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Alpaca API Key *
                    </label>
                    <input
                      type="password"
                      name="alpacaKey"
                      value={formData.alpacaKey}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      placeholder="Enter your Alpaca API key"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Alpaca Secret Key *
                    </label>
                    <input
                      type="password"
                      name="alpacaSecret"
                      value={formData.alpacaSecret}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-neutral-800 border border-neutral-600 rounded-lg px-4 py-3 text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      placeholder="Enter your Alpaca secret key"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-6 py-3 text-neutral-400 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-cyan-600 hover:bg-cyan-700 disabled:bg-neutral-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Account'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Setup Guide for users without accounts */}
          {accounts.length === 0 && (
            <div className="mb-8 bg-gradient-to-r from-green-900/20 to-blue-900/20 border border-green-500/50 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-green-400 mb-4">How to Get Started with Alpaca</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-white mb-3 flex items-center">
                    <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-2">1</span>
                    Create Alpaca Account
                  </h3>
                  <ul className="text-sm text-green-300 space-y-2">
                    <li>• Visit <a href="https://alpaca.markets" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">alpaca.markets</a></li>
                    <li>• Sign up for a free account</li>
                    <li>• Complete identity verification (required for trading)</li>
                    <li>• Start with paper trading (recommended for beginners)</li>
                    <li>• No minimum deposit required for paper trading</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-white mb-3 flex items-center">
                    <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-2">2</span>
                    Get API Keys
                  </h3>
                  <ul className="text-sm text-green-300 space-y-2">
                    <li>• Go to your Alpaca dashboard</li>
                    <li>• Navigate to "API Keys" section</li>
                    <li>• Generate new API key pair</li>
                    <li>• Copy both Key ID and Secret Key</li>
                    <li>• Keep your keys secure and private</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <h4 className="font-medium text-blue-400 mb-2">Pro Tips:</h4>
                <ul className="text-sm text-blue-300 space-y-1">
                  <li>• Start with paper trading to test strategies risk-free</li>
                  <li>• Use different keys for paper vs live trading</li>
                  <li>• Never share your API keys with anyone</li>
                  <li>• The system will automatically execute trades based on your preferences</li>
                </ul>
              </div>
            </div>
          )}

          {/* Accounts List */}
          <div className="bg-neutral-900 border border-neutral-700 rounded-lg overflow-hidden">
            {accounts.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-neutral-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Ready to Add Your Account?</h3>
                <p className="text-neutral-400 mb-4">Once you have your Alpaca API keys, add them below to start automated trading.</p>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Add Your First Account
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-800">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Account
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Environment
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        API Keys
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-medium text-neutral-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-700">
                    {accounts.map((account) => (
                      <tr key={account.id} className="hover:bg-neutral-800/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-white">
                              {account.name || 'Unnamed Account'}
                            </div>
                            <div className="text-sm text-neutral-400">ID: {account.id}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-neutral-300">
                            {account.accountType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEnvironmentColor(account.environment)}`}>
                            {account.environment}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(account.status)}`}>
                            {account.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-neutral-300">
                            {account.alpacaKey ? (
                              <div>
                                <div className="text-green-400">Key: {account.alpacaKey.substring(0, 8)}...</div>
                                <div className="text-green-400">Secret: {account.alpacaSecret ? account.alpacaSecret.substring(0, 8) + '...' : 'Not set'}</div>
                              </div>
                            ) : (
                              <span className="text-red-400">Not configured</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-300">
                          {new Date(account.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleDeleteAccount(account.id)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Security Notice */}
          <div className="mt-8 p-4 bg-blue-900/20 border border-blue-500/50 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-400">Security Notice</h3>
                <div className="mt-2 text-sm text-blue-300">
                  <p>
                    Your Alpaca API keys are encrypted and stored securely. Never share your API keys with anyone.
                    For paper trading, use your paper trading API keys. For live trading, ensure you understand the risks involved.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlpacaKeys;
